import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { IOfferingResponse, IDeityResponse } from 'types/api/temple';
import { TOfferingCategoryResponseMapType } from 'store/reducers/temple/offeringCategorySlice';
import { manjariBase64 } from './manjari';
import { capStr } from 'utils/fns';
import { fmtBlockPeriod, getTimeChipText, offeringTimeToForm } from './offering';

export type TDeityResponseMapType = Record<string, IDeityResponse>;

export const exportOfferingsToPDF = (
    offerings: IOfferingResponse[],
    categoryMap: TOfferingCategoryResponseMapType,
    deityMap: TDeityResponseMapType,
    templeName?: string
) => {
    const doc = new jsPDF('landscape');

    // Add custom font for Malayalam and English support
    doc.addFileToVFS("Manjari-Regular.ttf", manjariBase64);
    doc.addFont("Manjari-Regular.ttf", "Manjari", "normal");
    doc.setFont("Manjari");

    // Add Title
    const title = templeName ? `${templeName} - Offering List` : 'Offering List';
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

    // Define table columns
    const head = [[
        'S.No',
        'Ref No',
        'Name',
        'Category',
        'Price',
        'Deities',
        'Type',
        'Medium',
        'Pools',
        'Per Day Limit',
        'Block Period',
        'Time'
    ]];

    // Format table rows
    const body = offerings.map((offering, index) => {
        // Medium string calculation
        let medium = "N/A";
        if (offering.availableOnline && (offering.availableCounter || offering.availableKiosk || offering.availablePos)) {
            medium = "All";
        } else if (offering.availableCounter || offering.availableKiosk || offering.availablePos) {
            medium = [
                offering.availableCounter ? "Counter" : null,
                offering.availablePos ? "POS" : null,
                offering.availableKiosk ? "Kiosk" : null
            ].filter(Boolean).join(" | ");
        } else if (offering.availableOnline) {
            medium = "App/Web";
        }

        // Limit calculation
        let limitStr = 'N/A';
        if (offering.offeringTimes && offering.offeringTimes.length > 0) {
            const limits = offering.offeringTimes.flatMap(ot => ot.poolConnections.map(pc => pc?.pool?.poolLimit).filter(v => typeof v === "number" && !isNaN(v)) as number[]);
            if (limits.length === 0) {
                limitStr = 'No limit';
            } else {
                limitStr = limits.reduce((a, b) => a + b, 0).toString();
            }
        } else if (offering.poolConnections?.length > 0) {
            const min = Math.min(
                ...(offering.poolConnections)
                    .map(pc => pc?.pool?.poolLimit)
                    .filter(v => typeof v === "number" && !isNaN(v)) as number[]
            );
            limitStr = min === Infinity ? 'No limit' : min.toString();
        }

        let poolsStr = 'N/A';
        if (offering.offeringTimes && offering.offeringTimes.length > 0) {
            const pools = offering.offeringTimes.flatMap(ot => ot.poolConnections.map(pc => pc?.pool?.poolName).filter(Boolean));
            const uniquePools = Array.from(new Set(pools));
            poolsStr = uniquePools.length > 0 ? uniquePools.join(', ') : 'N/A';
        } else if (offering.poolConnections?.length > 0) {
            const pools = offering.poolConnections.map(pc => pc?.pool?.poolName).filter(Boolean);
            const uniquePools = Array.from(new Set(pools));
            poolsStr = uniquePools.length > 0 ? uniquePools.join(', ') : 'N/A';
        }

        const blockPeriodStr = offering.blockPeriod && offering.blockUnit ? fmtBlockPeriod(offering.blockPeriod, offering.blockUnit) : 'N/A';

        const timeStr = offering.offeringTimes && offering.offeringTimes.length > 0
            ? offering.offeringTimes.map(time => getTimeChipText(offeringTimeToForm(time))).join('\n')
            : 'N/A';

        return [
            index + 1,
            offering.templeRefNo ?? "N/A",
            offering.name,
            categoryMap[offering.category]?.name ?? "N/A",
            offering.price,
            offering.assignedDeities.map(d => deityMap[d]?.name).filter(Boolean).join(', ') || "N/A",
            capStr(offering.offeringType),
            medium,
            poolsStr,
            limitStr,
            blockPeriodStr,
            timeStr
        ];
    });

    autoTable(doc, {
        head,
        body,
        startY: 28,
        styles: { 
            fontSize: 8,
            font: "Manjari"
        },
        headStyles: { fillColor: [41, 128, 185] }, // Blue theme
        margin: { top: 28, right: 10, left: 10, bottom: 10 },
    });

    doc.save('Offering_List.pdf');
};
